import styled from 'styled-components';

import { Card } from '@components/card';
import { HtmlContent } from '@components/html-content';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useMinimumDelay } from '@hooks/use-minimum-delay';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useEmailDomains, useEmailSettings } from 'api/company/hooks';
import { useActionEmailTemplate } from 'api/journeys/hooks';

import {
  buildEmailPreviewProps,
  buildEmailPreviewPropsFromTravellerAction,
  getPreviewAttachments,
  PreviewEmailProps,
} from './email-preview';

import { AttachmentList } from '../common';
import {
  brokenChipStyles,
  renderWithVariableChips,
  variableChipStyles,
} from '../shared/variable-chips';

// Re-export pure helpers so existing call sites can keep importing from
// './email-preview-modal'. Tests should import from './email-preview'
// directly to avoid pulling React/browser-only modules in node test envs.
export { buildEmailPreviewProps, buildEmailPreviewPropsFromTravellerAction, getPreviewAttachments };
export type { PreviewEmailProps };

export const usePreviewEmailModal = () => useModal(PreviewEmail, { variant: 'LARGE' });

const EMPTY_EMAIL = '<p></p>';

type EmailItemProps = {
  infoText: string;
  text: string;
  isPlaceholder?: boolean;
  brokenLabels?: ReadonlySet<string>;
};

export const PreviewEmail = (props: PreviewEmailProps) => {
  const {
    to,
    subject,
    shortId,
    action,
    attachments,
    cc,
    bcc,
    replyTo,
    sendFromEmailId,
    liveContent,
    brokenLabels,
  } = props;
  const brokenLabelSet = brokenLabels?.length ? new Set(brokenLabels) : undefined;
  const { t } = useScopedTranslate(locale);
  const { data, isLoading } = useActionEmailTemplate(action, shortId, liveContent);
  // Floor the spinner's visible time so a fast response doesn't flash it.
  // `?? isLoading` covers the hook's initial null tick so it shows immediately.
  const isPreviewLoading = useMinimumDelay(isLoading, 500) ?? isLoading;

  const formatRecipientList = (
    value?: string | string[],
    emptyFallback: string | null = null
  ): string | null => {
    return Array.isArray(value) && value.length > 0 ? value.join(', ') : emptyFallback;
  };

  const hasRecipient = Array.isArray(to) && to.length > 0;
  const recipient = formatRecipientList(to, t('noRecipient')) as string;
  const ccInfo = formatRecipientList(cc);
  const bccInfo = formatRecipientList(bcc);
  const replyToInfo = formatRecipientList(replyTo);
  const hasSubject = Boolean(subject);
  const subjectInfo = subject ? subject : t('noSubject');
  const emailSettingsQuery = useEmailSettings();
  const emailSettings = emailSettingsQuery.data;
  const domainsQuery = useEmailDomains();
  const emailAttachments = attachments ?? [];

  const resolveFromAddress = (): { name: string; email: string } => {
    if (!sendFromEmailId || sendFromEmailId === 'system') {
      return { name: '50skills', email: 'noreply@mg.50skills.app' };
    }

    if (domainsQuery.data) {
      for (const domain of domainsQuery.data) {
        const address = domain.emailAddresses.find((addr) => addr.shortId === sendFromEmailId);
        if (address) {
          return { name: address.fromName, email: address.fromAddress };
        }
      }
    }

    return {
      name: emailSettings?.fromName ?? '50skills',
      email: emailSettings?.fromAddress ?? 'noreply@mg.50skills.app',
    };
  };

  const fromAddress = resolveFromAddress();

  return (
    <Modal>
      <Modal.SimpleHeader title={t('emailPreview')} />
      <ContentContainer>
        <Modal.Content>
          <Stack space={8}>
            <Stack space={2}>
              <Stack space={1}>
                <EmailItem
                  infoText={t('from')}
                  text={t('email', {
                    name: fromAddress.name,
                    email: fromAddress.email,
                  })}
                />
                <Separator />
              </Stack>
              <Stack space={1}>
                <EmailItem
                  infoText={t('to')}
                  text={recipient}
                  isPlaceholder={!hasRecipient}
                  brokenLabels={brokenLabelSet}
                />
                <Separator />
              </Stack>
              {ccInfo && (
                <Stack space={1}>
                  <EmailItem infoText={t('cc')} text={ccInfo} brokenLabels={brokenLabelSet} />
                  <Separator />
                </Stack>
              )}
              {bccInfo && (
                <Stack space={1}>
                  <EmailItem infoText={t('bcc')} text={bccInfo} brokenLabels={brokenLabelSet} />
                  <Separator />
                </Stack>
              )}
              {replyToInfo && (
                <Stack space={1}>
                  <EmailItem
                    infoText={t('replyTo')}
                    text={replyToInfo}
                    brokenLabels={brokenLabelSet}
                  />
                  <Separator />
                </Stack>
              )}
              <Stack space={1}>
                <EmailItem
                  infoText={t('subject')}
                  text={subjectInfo}
                  isPlaceholder={!hasSubject}
                  brokenLabels={brokenLabelSet}
                />
                {emailAttachments.length > 0 && <Separator />}
              </Stack>

              {emailAttachments.length > 0 && (
                <Stack space={4} align="top" orientation="horizontal">
                  <StyledText color="textSecondary" font="semibold14" text={t('attachments')} />
                  <Stack space={1}>
                    <AttachmentList attachments={emailAttachments} />
                  </Stack>
                </Stack>
              )}
            </Stack>
            <Card space={1}>
              <ThemedEmailContent>
                {/* Only the backend render is server-sanitized, so it's the only
                    value we inject as HTML -- never the raw author template. */}
                {isPreviewLoading ? (
                  <LoadingContainer>
                    <SizedRelativeSpinner size={32} />
                  </LoadingContainer>
                ) : (
                  <HtmlContent font="regular14" html={data || EMPTY_EMAIL} />
                )}
              </ThemedEmailContent>
            </Card>
          </Stack>
        </Modal.Content>
      </ContentContainer>
    </Modal>
  );
};

const EmailItem = (props: EmailItemProps) => {
  const { text, infoText, isPlaceholder, brokenLabels } = props;
  return (
    <Stack space={4} align="center" orientation="horizontal">
      <StyledText color="textSecondary" font="semibold14" text={infoText} />
      <Text
        font="regular14"
        color={isPlaceholder ? 'textHelper' : undefined}
        text={renderWithVariableChips(text, brokenLabels)}
      />
    </Stack>
  );
};

const ContentContainer = styled.div({
  overflowY: 'auto',
  marginTop: pxToRem(16),
});

const LoadingContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  padding: pxToRem(32),
});

const ThemedEmailContent = styled.div({
  '&, & *:not(a):not(code)': {
    color: `${theme.color.textPrimary} !important`,
  },
  '& a, & a *': {
    color: `${theme.color.elementBlue100} !important`,
  },
  '& code': {
    ...variableChipStyles,
    color: `${theme.color.elementBlue100} !important`,
  },
  // The backend wraps a broken variable (deleted source) in <ins>; render that
  // chip in red so it reads as broken rather than a normal mapped variable.
  '& ins': {
    textDecoration: 'none',
  },
  '& ins > code, & ins > code *': brokenChipStyles,
});

const StyledText = styled(Text)({
  width: pxToRem(120),
});

const locale = {
  en: {
    emailPreview: 'Email preview',
    from: 'FROM',
    to: 'TO',
    cc: 'CC',
    bcc: 'BCC',
    replyTo: 'REPLY-TO',
    subject: 'SUBJECT',
    poweredBy: 'Powered by 50skills',
    email: '{name} <{email}>',
    noContent: 'Email has no content',
    noSubject: 'Email has no subject',
    noRecipient: 'Not set',
    attachments: 'ATTACHMENTS',
  },
};
